/**
 * Age & State Gate — age (DOB), then shipping state when the state slide is in the DOM and `shipping_state_mf` is not set.
 * - If `age_verified` cookie is already `1` and state is needed (no `shipping_state_mf`) → open directly on state slide, skipping DOB.
 * - If the state slide exists but the cookie is already set → state step is skipped entirely.
 * - Step 1 passes → sessionStorage `hallwines_age_state_gate_ok` only when a state step will be shown.
 * - Age-only (no state slide) → `age_only`; clears `shipping_state_mf` + cart `shipping_state`.
 * - Full flow → cart + `shipping_state_mf` via the merged `cross-domain-cart-sync.js`.
 */
(function () {
  'use strict';

  var SESSION_AGE = 'hallwines_age_state_gate_ok';
  var HTML_CLASS = 'age-state-gate-verified';

  var FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var bodyInertTargets = [];

  var stateSelectorReopened = false;
  var stateSelectorReopenTrigger = null;

  function parseRestrictedStateCodes(rawCodes) {
    if (!rawCodes) return [];
    return String(rawCodes)
      .split('|')
      .map(function (code) { return code.trim().toUpperCase(); })
      .filter(function (code) { return code !== ''; });
  }

  function getFocusableIn(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapTab(root, event) {
    if (event.key !== 'Tab') return;
    var panel = root.querySelector('[data-age-state-gate-panel]');
    if (!panel) return;
    var focusable = getFocusableIn(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function applyBackgroundInert(root) {
    if (bodyInertTargets.length) return;
    var gate = root.closest('#overlay-group') || root.parentElement;
    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child === gate || child.contains(root)) return;
      if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') return;
      if (child.hasAttribute('inert')) return;
      child.setAttribute('inert', '');
      child.setAttribute('aria-hidden', 'true');
      bodyInertTargets.push(child);
    });
  }

  function removeBackgroundInert() {
    bodyInertTargets.forEach(function (el) {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    });
    bodyInertTargets = [];
  }

  function triggerPendingLoginPrompt() {
    var fn = /** @type {any} */ (window).__hallwinesTriggerPendingLoginPrompt;
    if (typeof fn === 'function') fn();
  }

  function parseInt10(s) {
    return parseInt(String(s == null ? '' : s).trim(), 10);
  }

  function isValidDate(y, m, d) {
    if (m < 1 || m > 12 || d < 1 || d > 31) return false;
    var dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
  }

  /**
   * @returns {{ ok: boolean, under?: boolean, invalid?: boolean }}
   */
  function checkAge21(monthStr, dayStr, yearStr) {
    var m = parseInt10(monthStr);
    var d = parseInt10(dayStr);
    var y = parseInt10(yearStr);
    if (!/^\d{1,2}$/.test(String(monthStr == null ? '' : monthStr).trim())) return { invalid: true };
    if (!/^\d{1,2}$/.test(String(dayStr == null ? '' : dayStr).trim())) return { invalid: true };
    if (!/^\d{4}$/.test(String(yearStr == null ? '' : yearStr).trim())) return { invalid: true };
    if (!isValidDate(y, m, d)) return { invalid: true };

    var today = new Date();
    var birth = new Date(y, m - 1, d);
    if (birth > today) return { invalid: true };

    var twentyFirst = new Date(y + 21, m - 1, d);
    if (today < twentyFirst) return { under: true };
    return { ok: true };
  }

  function getRoot(el) {
    return el && el.closest ? el.closest('[data-age-state-gate-root]') : null;
  }

  function announceSlide(root, index) {
    var live = root.querySelector('[data-age-state-gate-announce]');
    if (!live) return;
    var msg =
      index === 1
        ? root.getAttribute('data-announce-state') || ''
        : root.getAttribute('data-announce-age') || '';
    // Clear first so AT re-announces even when the step label repeats.
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = msg;
    }, 50);
  }

  function setSlide(root, index) {
    var ageSlide = root.querySelector('[data-age-state-gate-slide="age"]');
    var stateSlide = root.querySelector('[data-age-state-gate-slide="state"]');
    var dots = root.querySelectorAll('[data-age-state-gate-dot]');
    if (!ageSlide) return;
    if (index === 1 && !stateSlide) return;

    if (index === 0) {
      ageSlide.classList.remove('age-state-gate__slide--hidden');
      ageSlide.removeAttribute('aria-hidden');
      if (stateSlide) {
        stateSlide.classList.add('age-state-gate__slide--hidden');
        stateSlide.setAttribute('aria-hidden', 'true');
      }
    } else {
      ageSlide.classList.add('age-state-gate__slide--hidden');
      ageSlide.setAttribute('aria-hidden', 'true');
      if (stateSlide) {
        stateSlide.classList.remove('age-state-gate__slide--hidden');
        stateSlide.removeAttribute('aria-hidden');
      }
    }

    dots.forEach(function (dot, i) {
      dot.classList.toggle('age-state-gate__dot--active', i === index);
    });

    announceSlide(root, index);
  }

  function getShippingStateMf() {
    if (typeof window.__getShippingStateFromCookie === 'function') {
      return window.__getShippingStateFromCookie() || '';
    }
    return '';
  }

  function isAgeVerified() {
    if (typeof window.__isAgeVerifiedCookie === 'function') {
      return !!window.__isAgeVerifiedCookie();
    }
    return /(?:^|; )age_verified=1(?:;|$)/.test(document.cookie);
  }

  function isStateSkipped() {
    var fn = /** @type {any} */ (window).__isStateSkippedCookie;
    if (typeof fn === 'function') return !!fn();
    return /(?:^|; )shipping_state_mf_skipped=1(?:;|$)/.test(document.cookie);
  }

  // Mirrors age/state persistence: cookie + cart attr so the skip survives checkout.
  function persistStateSkipped() {
    if (document.documentElement.classList.contains('shopify-design-mode')) return;
    var markFn = /** @type {any} */ (window).__markStateSkipped;
    if (typeof markFn === 'function') markFn();
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          age_verified: '1',
          shipping_state_skipped: '1',
          age_gate_flow: 'skipped'
        }
      })
    }).catch(function () {});
  }

  function persistAgeCookie() {
    if (document.documentElement.classList.contains('shopify-design-mode')) return;
    if (typeof window.__writeAgeVerifiedCookie === 'function') {
      window.__writeAgeVerifiedCookie();
    }
  }

  function completeFullFlow(root, st) {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      return;
    }
    if (!st) return;

    try {
      sessionStorage.removeItem(SESSION_AGE);
    } catch (e) {}

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          age_verified: '1',
          shipping_state: st,
          age_gate_flow: 'full'
        }
      })
    })
      .then(function () {
        if (typeof window.__markAgeVerified === 'function') {
          window.__markAgeVerified();
        } else {
          document.documentElement.classList.add(HTML_CLASS);
        }
        if (typeof window.__markShippingState === 'function') {
          window.__markShippingState(st);
        }

        // Reload so the Michel Foch ship-to restriction Liquid (rendered
        // before state was set) re-evaluates against the just-written value.
        if (document.querySelector('[data-buy-buttons-michel-foch]')) {
          try { sessionStorage.setItem('age-verified-synced', '1'); } catch (e) {}
          window.location.reload();
          return;
        }

        removeBackgroundInert();
        try {
          sessionStorage.setItem('age-verified-synced', '1');
        } catch (e) {}
        if (typeof window.__hallwinesCrossDomainPatchLinks === 'function') {
          window.__hallwinesCrossDomainPatchLinks();
        }
        triggerPendingLoginPrompt();
      })
      .catch(function () {
        if (typeof window.__markAgeVerified === 'function') {
          window.__markAgeVerified();
        }
      });
  }

  function completeAgeOnly(root) {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      return;
    }

    try {
      sessionStorage.removeItem(SESSION_AGE);
    } catch (e) {}

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          age_verified: '1',
          age_gate_flow: 'age_only',
          shipping_state: null
        }
      })
    })
      .then(function () {
        if (typeof window.__clearShippingStateCookie === 'function') {
          window.__clearShippingStateCookie();
        }
        if (typeof window.__markAgeVerified === 'function') {
          window.__markAgeVerified();
        } else {
          document.documentElement.classList.add(HTML_CLASS);
        }
        removeBackgroundInert();
        try {
          sessionStorage.setItem('age-verified-synced', '1');
        } catch (e) {}
        if (typeof window.__hallwinesCrossDomainPatchLinks === 'function') {
          window.__hallwinesCrossDomainPatchLinks();
        }
        triggerPendingLoginPrompt();
      })
      .catch(function () {
        if (typeof window.__markAgeVerified === 'function') {
          window.__markAgeVerified();
        }
      });
  }

  function openStateSelector() {
    var root = document.querySelector('[data-age-state-gate-root]');
    if (!root) return;
    var stateSlideEl = root.querySelector('[data-age-state-gate-slide="state"]');
    if (!stateSlideEl) return;

    stateSelectorReopened = true;
    stateSelectorReopenTrigger =
      document.activeElement && document.activeElement !== document.body ? document.activeElement : null;

    var stateSelect = root.querySelector('[data-age-state-gate-state-select]');
    if (stateSelect) {
      var current = getShippingStateMf();
      if (current) /** @type {HTMLSelectElement} */ (stateSelect).value = current;
      stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setSlide(root, 1);
    root.classList.add('age-state-gate--show');
    // Suppresses the dots — gate's `:has(state-slide)` CSS would otherwise show them.
    root.classList.add('age-state-gate--reopen');
    applyBackgroundInert(root);

    if (stateSelect) {
      setTimeout(function () { /** @type {HTMLElement} */ (stateSelect).focus(); }, 0);
    }
  }

  // Reload so server-side restriction Liquid re-evaluates against the new state.
  function completeStateUpdate(root, st) {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      closeStateSelectorReopen(root);
      return;
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          age_verified: '1',
          shipping_state: st,
          shipping_state_skipped: '',
          age_gate_flow: 'full'
        }
      })
    })
      .then(function () {
        if (typeof window.__markShippingState === 'function') window.__markShippingState(st);
        var clr = /** @type {any} */ (window).__clearStateSkippedCookie;
        if (typeof clr === 'function') clr();
        window.location.reload();
      })
      .catch(function () {
        window.location.reload();
      });
  }

  // No skip persisted — visitor was already verified before the reopen.
  function closeStateSelectorReopen(root) {
    stateSelectorReopened = false;
    root.classList.remove('age-state-gate--show');
    root.classList.remove('age-state-gate--reopen');
    removeBackgroundInert();
    var stateSlideEl = root.querySelector('[data-age-state-gate-slide="state"]');
    if (stateSlideEl) {
      stateSlideEl.classList.add('age-state-gate__slide--hidden');
      stateSlideEl.setAttribute('aria-hidden', 'true');
    }
    var trigger = stateSelectorReopenTrigger;
    stateSelectorReopenTrigger = null;
    if (trigger && typeof (/** @type {any} */ (trigger).focus) === 'function') {
      try { /** @type {HTMLElement} */ (trigger).focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function digitsInput(el, maxLen, onFull, onInput) {
    el.addEventListener('input', function () {
      var v = el.value.replace(/\D/g, '').slice(0, maxLen);
      if (v !== el.value) el.value = v;
      el.setAttribute('aria-invalid', 'false');
      if (typeof onInput === 'function') onInput();
      if (onFull && v.length === maxLen) onFull();
    });
  }

  function initRoot(root) {
    var isDesignMode = document.documentElement.classList.contains('shopify-design-mode');
    var forceShowInEditor = root.getAttribute('data-age-state-gate-force-show') === 'true';
    /** When true, treat the visitor as unverified even if cookies/cart say otherwise — used in the theme editor
        with `force_show_in_editor` on, so merchants can configure the modal even when they've personally
        verified in their own browser. Outside the editor this is always false; inside the editor it's false
        unless the toggle is on, which means the modal respects verification state the same as on storefront. */
    var ignoreVerified = isDesignMode && forceShowInEditor;

    var previouslyFocused =
      document.activeElement && document.activeElement !== document.body ? document.activeElement : null;

    function restorePreviousFocus() {
      var target = previouslyFocused || document.getElementById('MainContent');
      if (!target) return;
      try {
        if (target.tabIndex < 0 && !target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: true });
      } catch (e) {}
    }

    var closeBtn = root.querySelector('[data-age-state-gate-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (stateSelectorReopened) {
          closeStateSelectorReopen(root);
          return;
        }
        persistStateSkipped();
        root.classList.remove('age-state-gate--show');
        removeBackgroundInert();
        if (typeof window.__markAgeVerified === 'function') {
          window.__markAgeVerified();
        } else {
          document.documentElement.classList.add(HTML_CLASS);
        }
        restorePreviousFocus();
        triggerPendingLoginPrompt();
      });
    }

    var ageSlideEl = root.querySelector('[data-age-state-gate-slide="age"]');
    var stateSlideEl = root.querySelector('[data-age-state-gate-slide="state"]');
    var stateInDom = !!stateSlideEl;

    if (!stateInDom) {
      var titleEl = root.querySelector('[data-age-state-gate-title]');
      var ageOnlyLabel = root.getAttribute('data-dialog-label-age-only');
      if (titleEl && ageOnlyLabel) titleEl.textContent = ageOnlyLabel;
    }

    /** The html class at init only comes from Liquid (cart attrs) or the FOUC
        script (cookies/params), both of which already encode the full brand
        requirement — so it can be trusted even when the local cookie cache is
        stale (cleared cookies, Safari's 7-day cap on JS-written cookies). The
        async reconcile rehydrates the cookies in the background. */
    var verifiedAtInit = !ignoreVerified && document.documentElement.classList.contains(HTML_CLASS);
    var cookieStateAtInit = ignoreVerified ? '' : getShippingStateMf();
    var ageAlreadyVerified = ignoreVerified ? false : verifiedAtInit || isAgeVerified();
    var stateSkipped = ignoreVerified ? false : isStateSkipped();
    /** When the brand shows the state step but `shipping_state_mf` is already set, skip the state UI. */
    var needsStateUI = stateInDom && (ignoreVerified || (!verifiedAtInit && !cookieStateAtInit && !stateSkipped));
    /** When age is already verified and state is still needed, open the overlay directly on the state slide. */
    var skipAgeSlide = needsStateUI && ageAlreadyVerified;

    if (stateInDom && !ignoreVerified && cookieStateAtInit) {
      stateSlideEl.classList.add('age-state-gate__slide--hidden');
      stateSlideEl.setAttribute('aria-hidden', 'true');
    }

    // In the editor, visibility is driven solely by `force_show_in_editor`.
    var shouldShow = isDesignMode
      ? forceShowInEditor
      : (!ageAlreadyVerified || needsStateUI);

    if (needsStateUI && document.documentElement.classList.contains(HTML_CLASS)) {
      try {
        document.documentElement.classList.remove(HTML_CLASS);
      } catch (e) {}
    }

    var dotsWrap = root.querySelector('[data-age-state-gate-dots]');
    if (dotsWrap) {
      dotsWrap.classList.toggle('age-state-gate__dots--visible', needsStateUI && !skipAgeSlide);
    }

    if (!stateInDom || !needsStateUI) {
      try {
        sessionStorage.removeItem(SESSION_AGE);
      } catch (e) {}
    }

    if (skipAgeSlide && ageSlideEl && stateSlideEl) {
      ageSlideEl.classList.add('age-state-gate__slide--hidden');
      ageSlideEl.setAttribute('aria-hidden', 'true');
      stateSlideEl.classList.remove('age-state-gate__slide--hidden');
      stateSlideEl.removeAttribute('aria-hidden');
      try {
        sessionStorage.setItem(SESSION_AGE, '1');
      } catch (e) {}
    }

    var under21DefaultPath = '/';
    var rawUnder = root.getAttribute('data-under-21-url');
    var underUrl =
      rawUnder != null && String(rawUnder).trim() !== '' ? String(rawUnder).trim() : under21DefaultPath;

    try {
      var u = new URL(underUrl, window.location.origin);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') {
        underUrl = new URL(under21DefaultPath, window.location.origin).href;
      } else {
        underUrl = u.href;
      }
    } catch (e) {
      underUrl = new URL(under21DefaultPath, window.location.origin).href;
    }

    var monthEl = root.querySelector('[data-age-state-gate-dob-month]');
    var dayEl = root.querySelector('[data-age-state-gate-dob-day]');
    var yearEl = root.querySelector('[data-age-state-gate-dob-year]');
    var dobErrorEl = root.querySelector('[data-age-state-gate-dob-error]');
    var ageBtn = root.querySelector('[data-age-state-gate-continue]');
    var stateSelect = root.querySelector('[data-age-state-gate-state-select]');
    var stateErrorEl = root.querySelector('[data-age-state-gate-state-error]');
    var doneBtn = root.querySelector('[data-age-state-gate-state-done]');
    var panel = root.querySelector('[data-age-state-gate-panel]');

    // Each block owns its own error message — read it off the error slot, with locale fallback baked in by Liquid.
    var dobErr = (dobErrorEl && dobErrorEl.getAttribute('data-error-message')) || '';
    var stateErr = (stateErrorEl && stateErrorEl.getAttribute('data-error-message')) || '';
    var restrictedStateCodes = parseRestrictedStateCodes(
      stateSelect ? stateSelect.getAttribute('data-age-state-gate-restricted-codes') : ''
    );
    var stateRestrictedTemplate = stateSelect
      ? (stateSelect.getAttribute('data-age-state-gate-restricted-message') || '')
      : '';

    if (monthEl && dayEl && yearEl) {
      // All three fields clear the unified DOB error on input — validation runs
      // only on Continue, so the message stays in sync with the actual submit.
      digitsInput(monthEl, 2, function () { dayEl.focus(); }, function () { clearDobError(); });
      digitsInput(dayEl, 2, function () { yearEl.focus(); }, function () { clearDobError(); });
      digitsInput(yearEl, 4, null, function () { clearDobError(); });

      [monthEl, dayEl].forEach(function (el) {
        el.addEventListener('blur', function () {
          if (el.value.length === 1) el.value = '0' + el.value;
        });
      });
    }

    function showDobError(msg) {
      if (dobErrorEl && dobErrorEl.textContent !== msg) {
        // Clear + defer so role="alert" re-announces when the message actually changes.
        // No-op when the same message is already visible — avoids a flicker/layout shift on repeat Continue clicks.
        dobErrorEl.textContent = '';
        window.setTimeout(function () {
          dobErrorEl.textContent = msg;
        }, 50);
      }
      ;[monthEl, dayEl, yearEl].forEach(function (el) {
        if (el) el.setAttribute('aria-invalid', 'true');
      });
    }

    function clearDobError() {
      if (dobErrorEl) {
        dobErrorEl.textContent = '';
      }
      ;[monthEl, dayEl, yearEl].forEach(function (el) {
        if (el) el.setAttribute('aria-invalid', 'false');
      });
    }

    function showStateError(msg) {
      if (stateErrorEl && stateErrorEl.textContent !== msg) {
        stateErrorEl.textContent = '';
        window.setTimeout(function () {
          stateErrorEl.textContent = msg;
        }, 50);
      }
      if (stateSelect) stateSelect.setAttribute('aria-invalid', 'true');
    }

    function clearStateError() {
      if (stateErrorEl) {
        stateErrorEl.textContent = '';
      }
      if (stateSelect) stateSelect.setAttribute('aria-invalid', 'false');
    }

    function isRestrictedState(code) {
      var normalized = String(code || '').trim().toUpperCase();
      if (!normalized) return false;
      return restrictedStateCodes.indexOf(normalized) !== -1;
    }

    function getSelectedStateLabel() {
      if (!stateSelect) return '';
      var selected =
        stateSelect.selectedIndex >= 0 ? stateSelect.options[stateSelect.selectedIndex] : null;
      return selected ? String(selected.textContent || '').trim() : '';
    }

    function getRestrictedStateMessage(code) {
      if (!stateRestrictedTemplate) return '';
      var stateLabel = getSelectedStateLabel() || String(code || '').trim().toUpperCase();
      return stateRestrictedTemplate.replace('[STATE]', stateLabel);
    }

    function validateRestrictedStateSelection() {
      if (!stateSelect) return false;
      var code = String(stateSelect.value || '').trim().toUpperCase();
      if (!code) {
        clearStateError();
        return false;
      }
      if (!isRestrictedState(code)) {
        clearStateError();
        return false;
      }
      showStateError(getRestrictedStateMessage(code));
      return true;
    }

    try {
      if (needsStateUI && (skipAgeSlide || sessionStorage.getItem(SESSION_AGE) === '1')) {
        setSlide(root, 1);
        if (stateSelect) setTimeout(function () { stateSelect.focus(); }, 0);
      }
    } catch (e) {}

    if (ageBtn && monthEl && dayEl && yearEl) {
      [monthEl, dayEl, yearEl].forEach(function (el) {
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            ageBtn.click();
          }
        });
      });

      ageBtn.addEventListener('click', function () {
        var res = checkAge21(monthEl.value, dayEl.value, yearEl.value);
        if (res.invalid) {
          // Idempotent — repeat clicks with the same invalid input don't re-render the message.
          showDobError(dobErr);
          monthEl.focus();
          return;
        }
        if (res.under) {
          window.location.href = underUrl;
          return;
        }
        clearDobError();
        persistAgeCookie();
        if (!stateInDom) {
          completeAgeOnly(root);
          return;
        }
        var fromCookie = ignoreVerified ? '' : getShippingStateMf();
        if (fromCookie) {
          completeFullFlow(root, fromCookie);
          return;
        }
        if (!ignoreVerified) {
          try {
            sessionStorage.setItem(SESSION_AGE, '1');
          } catch (e) {}
        }
        setSlide(root, 1);
        if (stateSelect) {
          stateSelect.focus();
        }
      });
    }

    if (doneBtn && stateSelect) {
      stateSelect.addEventListener('change', function () {
        validateRestrictedStateSelection();
      });

      doneBtn.addEventListener('click', function () {
        var st = stateSelect.value;
        if (!st) {
          showStateError(stateErr);
          stateSelect.focus();
          return;
        }
        // A restricted state keeps showing the informational error (set on
        // `change`) but no longer blocks submit — the visitor can save it and
        // close. The actual purchase gate is enforced server-side on the PDP
        // (Michel Foch ship-to restriction re-evaluates after the state write).
        validateRestrictedStateSelection();

        if (stateSelectorReopened) {
          completeStateUpdate(root, st);
          return;
        }
        completeFullFlow(root, st);
      });
    }

    // Bound unconditionally so the trap covers the on-demand reopen path
    // where `shouldShow` is false at init.
    root.addEventListener('keydown', function (e) {
      trapTab(root, e);
    });

    if (panel && !shouldShow) {
      triggerPendingLoginPrompt();
    }

    if (panel && shouldShow) {
      root.classList.add('age-state-gate--show');
      applyBackgroundInert(root);

      // Handle async HTML_CLASS additions from reconcileLocalFromCart — but keep
      // the gate up if state is still needed, since that path sets the class on
      // age alone and would otherwise hide the state slide prematurely.
      if ('MutationObserver' in window) {
        var htmlEl = document.documentElement;
        var observer = new MutationObserver(function () {
          if (!htmlEl.classList.contains(HTML_CLASS)) return;
          // A reconciled skip flag satisfies the state step the same as a real
          // state — without this the gate would stay up after the async
          // cart → cookie restore of `shipping_state_mf_skipped`.
          if (needsStateUI && !getShippingStateMf() && !isStateSkipped()) return;
          root.classList.remove('age-state-gate--show');
          removeBackgroundInert();
          observer.disconnect();
          triggerPendingLoginPrompt();
        });
        observer.observe(htmlEl, { attributes: true, attributeFilter: ['class'] });
      }

      if (skipAgeSlide) {
        if (stateSelect) {
          setTimeout(function () {
            stateSelect.focus();
          }, 0);
        }
      } else {
        var first = root.querySelector('[data-age-state-gate-slide="age"] [data-age-state-gate-dob-month]');
        if (first && sessionStorage.getItem(SESSION_AGE) !== '1') {
          setTimeout(function () {
            first.focus();
          }, 0);
        }
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-age-state-gate-root]').forEach(initRoot);
  }

  /** @type {any} */ (window).__hallwinesOpenStateSelector = openStateSelector;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
